/**
 * Black Friday → one brand's terms — /black-friday/:year/:brandId
 *
 * Left: this season's terms (contact & asks, sell-out, sell-in with tiers,
 * top-up, notes), saved field by field. Right: the prep card — every
 * earlier season for this brand, for Robbie's negotiation (not for the
 * outreach email).
 */
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AttachmentsSection from '../../components/attachments/AttachmentsSection';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Checkbox } from '../../components/ui/Checkbox';
import { CommitInput } from '../../components/ui/CommitInput';
import { Field } from '../../components/ui/Field';
import { Icon } from '../../components/ui/Icon';
import { useToast } from '../../components/ui/Toast';
import { useBrand } from '../../lib/api/brands';
import {
  ASKS,
  AskState,
  BF_SECTIONS,
  BFSectionKey,
  BFStatus,
  BrandTerms,
  PARTICIPATES_LABEL,
  Participates,
  RegionalCap,
  SCOPE_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
  Scope,
  TermsPatch,
  Tier,
  YESNO_LABEL,
  YesNo,
  fmtDay,
  fmtEurShort,
  fmtPct,
  findLinks,
  noteHead,
  num,
  selloutLine,
  sellinLine,
  tierLine,
  siteDiscountLine,
  useBFOwners,
  useBFPriority,
  useBFSeason,
  useBrandBFHistory,
  useTermsMutations,
} from '../../lib/api/blackFriday';
import { BrandDot, StatusPill } from './BFRegister';
import { BF_STYLES } from './styles';

const decStr = (v: string | null) => (num(v) === null ? '' : String(num(v)));
const decOut = (v: string) => (v.trim() === '' ? null : v.trim().replace(',', '.'));

export const BFBrandTerms = () => {
  const { year: yearParam, brandId } = useParams<{ year: string; brandId: string }>();
  const year = parseInt(yearParam || '', 10) || new Date().getFullYear();
  const navigate = useNavigate();
  const toast = useToast();
  const brand = useBrand(brandId);
  const season = useBFSeason(year);
  const history = useBrandBFHistory(brandId);
  const m = useTermsMutations();

  const current = history.data?.find((t) => t.year === year);
  const earlier = (history.data ?? []).filter((t) => t.year !== year);

  return (
    <div className="bf">
      <style>{BF_STYLES}</style>
      <div className="page-header">
        <div>
          <button type="button" className="link" style={{ fontSize: 12, display: 'inline-block', marginBottom: 14 }} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(`/black-friday/${year}`))}>
            ← Brands & terms {year}
          </button>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {brand.data && <BrandDot name={brand.data.name} logo={brand.data.logo ?? undefined} color={brand.data.color} />}
            {brand.data?.name ?? brandId}
          </h1>
        </div>
        {current && <HeaderActions t={current} seasonHasCampaign={!!season.data?.campaign} />}
      </div>

      {history.isLoading || season.isLoading ? (
        <div className="empty">Loading…</div>
      ) : !season.data ? (
        <Card>
          <div className="empty" style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
            <div>There is no Black Friday {year} season yet.</div>
            <Button variant="primary" onClick={() => navigate(`/black-friday/${year}`)}>Start it on the Brands & terms page</Button>
          </div>
        </Card>
      ) : !current ? (
        <div className="grid2">
          <Card>
            <div className="empty" style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
              <div>{brand.data?.name ?? 'This brand'} is not on the {year} register yet.</div>
              <Button
                variant="primary"
                disabled={m.create.isPending || !brandId}
                onClick={() => brandId && m.create.mutate({ season: season.data!.id, brand: brandId }, {
                  onError: () => toast.push('Could not add the brand'),
                })}
              >
                <Icon name="plus" size={13} /> Add to the {year} register
              </Button>
            </div>
          </Card>
          <PrepCard rows={earlier} />
        </div>
      ) : (
        <div className="grid2">
          <TermsEditor t={current} />
          <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
            <div className="bf-files">
              <AttachmentsSection
                bfTermsId={current.id}
                title="Files"
                emptyText="No files yet. Drop the brand's exclusion list, price file or presentation here."
              />
            </div>
            <NumbersCard year={year} brandId={current.brand} />
            <PrepCard rows={earlier} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BFBrandTerms;

function HeaderActions({ t, seasonHasCampaign }: { t: BrandTerms; seasonHasCampaign: boolean }) {
  const m = useTermsMutations();
  const toast = useToast();
  const navigate = useNavigate();
  const owners = useBFOwners();
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        className={`cell-select pill st-${t.status}`}
        value={t.status}
        onChange={(e) => m.patch.mutate({ id: t.id, status: e.target.value as BFStatus }, { onError: () => toast.push('Could not save the status') })}
      >
        {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
      </select>
      <select
        className="input"
        style={{ maxWidth: 170 }}
        value={t.owner ?? ''}
        onChange={(e) => m.patch.mutate({ id: t.id, owner: e.target.value ? parseInt(e.target.value, 10) : null }, { onError: () => toast.push('Could not save the owner') })}
      >
        <option value="">No owner</option>
        {(owners.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      {t.source_url && (
        <Button variant="ghost" onClick={() => window.open(t.source_url, '_blank', 'noopener')}>
          <Icon name="mail" size={13} /> Email thread
        </Button>
      )}
      {t.discount_asset ? (
        <Button variant="ghost" onClick={() => navigate(`/campaigns/${t.discount_asset_campaign}`)}>
          <Icon name="tag" size={13} /> Discount placement
        </Button>
      ) : (
        <Button
          variant="primary"
          disabled={!seasonHasCampaign || m.createPlacement.isPending}
          title={seasonHasCampaign ? 'Creates the brand-tagged Discount placement on the Black Week campaign' : 'Link the season to a campaign first'}
          onClick={() => m.createPlacement.mutate(t.id, {
            onSuccess: () => toast.push('Discount placement added to the campaign'),
            onError: (e: unknown) => toast.push((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Could not create the placement'),
          })}
        >
          <Icon name="plus" size={13} /> Add to Black Week
        </Button>
      )}
    </div>
  );
}

function TermsEditor({ t }: { t: BrandTerms }) {
  const m = useTermsMutations();
  const toast = useToast();
  const save = (patch: TermsPatch) =>
    m.patch.mutate({ id: t.id, ...patch }, {
      onError: (e: unknown) => {
        const data = (e as { response?: { data?: Record<string, unknown> } })?.response?.data;
        toast.push(data ? `Not saved: ${Object.values(data).flat().join(' ')}` : 'Could not save');
      },
    });
  const pct = (value: string | null, key: keyof TermsPatch) => (
    <Unit unit="%"><CommitInput type="number" min={0} value={decStr(value)} onCommit={(v) => save({ [key]: decOut(v) } as TermsPatch)} /></Unit>
  );
  const eur = (value: string | null, key: keyof TermsPatch) => (
    <Unit unit="€ net"><CommitInput type="number" min={0} value={decStr(value)} onCommit={(v) => save({ [key]: decOut(v) } as TermsPatch)} /></Unit>
  );
  const day = (value: string | null, key: keyof TermsPatch) => (
    <CommitInput type="date" value={value ?? ''} onCommit={(v) => save({ [key]: v || null } as TermsPatch)} />
  );

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <SaveState pending={m.patch.isPending} savedAt={m.patch.isSuccess ? m.patch.submittedAt : 0} />

      <Section k="contact" summary={asksSummary(t)}>
        <Group title="Who we deal with">
          <div className="form-grid">
            <Field label="Contact"><CommitInput value={t.contact} placeholder="Name <email>" onCommit={(v) => save({ contact: v })} /></Field>
            <Field label="Agent / distributor"><CommitInput value={t.agent} placeholder="e.g. visaversa.be" onCommit={(v) => save({ agent: v })} /></Field>
            <Field label="Email thread or terms file">
              <CommitInput value={t.source_url} placeholder="https://…" onCommit={(v) => save({ source_url: v })} />
              <Links text={t.source_url} />
            </Field>
            <Field label="Asked on">{day(t.asked_at, 'asked_at')}</Field>
            <Field label="Answered on">{day(t.answered_at, 'answered_at')}</Field>
          </div>
          <label className="check">
            <Checkbox checked={t.brand_initiated} onChange={(v) => save({ brand_initiated: v })} />
            The brand sent its own Black Friday campaign
          </label>
        </Group>
        <Group title={<>The six standard asks{asksCount(t) && <span className="grp-count">· {asksCount(t)}</span>}</>}>
          {ASKS.map((a) => (
            <div key={a.key} className={`ask-row st-${t.asks?.[a.key] ?? 'open'}`}>
              <span className="ask-label">
                <span className="ask-mark" aria-hidden="true">
                  {(t.asks?.[a.key] ?? 'open') === 'yes' ? <Icon name="check" size={11} /> : (t.asks?.[a.key] ?? 'open') === 'no' ? <Icon name="x" size={11} /> : null}
                </span>
                {a.label}
              </span>
              <span className="seg">
                {(['open', 'yes', 'no'] as AskState[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={s}
                    aria-pressed={(t.asks?.[a.key] ?? 'open') === s}
                    onClick={() => save({ asks: { ...t.asks, [a.key]: s } })}
                  >
                    {s === 'open' ? 'Open' : s === 'yes' ? 'Agreed' : 'Refused'}
                  </button>
                ))}
              </span>
            </div>
          ))}
        </Group>
      </Section>

      <Section k="sellout" alias="sell-out" summary={selloutLine(t)}>
        <Group title="What the brand allows on lamptwist.com">
          <div className="form-grid">
            <Field label="Participates">
              <select className="input" value={t.participates} onChange={(e) => save({ participates: e.target.value as Participates })}>
                {Object.entries(PARTICIPATES_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field label="Recommended discount">{pct(t.discount_recommended_pct, 'discount_recommended_pct')}</Field>
            <Field label="Maximum discount">{pct(t.discount_max_pct, 'discount_max_pct')}</Field>
            <Field label="Scope">
              <select className="input" value={t.scope} onChange={(e) => save({ scope: e.target.value as Scope })}>
                {Object.entries(SCOPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field label="Allowed from">{day(t.promo_start, 'promo_start')}</Field>
            <Field label="Allowed until">{day(t.promo_end, 'promo_end')}</Field>
          </div>
          <Field label="Different maximum per region">
            <RegionalCapsEditor value={t.regional_caps} onSave={(caps) => save({ regional_caps: caps })} />
          </Field>
          <Field label="Exclusions, territory, display rules">
            <CommitInput multiline rows={3} value={t.sellout_notes} onCommit={(v) => save({ sellout_notes: v })} placeholder="e.g. novelties excluded, no UK, show RRP crossed out" />
            <Links text={t.sellout_notes} />
          </Field>
        </Group>
        <Group title="Visibility conditions">
          <Field label="What the brand asks from us in return">
            <CommitInput multiline rows={3} value={t.visibility_conditions} onCommit={(v) => save({ visibility_conditions: v })} placeholder="e.g. homepage or main menu for at least 3 days, 1 newsletter mention, 1 social post" />
            <Links text={t.visibility_conditions} />
          </Field>
        </Group>
        <Group title="What we ran">
          <div className="form-grid">
            <Field label="Discount on the site">{pct(t.our_discount_pct, 'our_discount_pct')}</Field>
            <Field label="Live from">{day(t.our_promo_start, 'our_promo_start')}</Field>
          </div>
        </Group>
      </Section>

      <Section k="sellin" alias="sell-in" summary={sellinLine(t)}>
        <Group title="Terms">
          <div className="form-grid">
            <Field label="Extra discount">{pct(t.extra_discount_pct, 'extra_discount_pct')}</Field>
            <Field label="Minimum order">{eur(t.minimum_order_net, 'minimum_order_net')}</Field>
            <Field label="Order deadline">{day(t.order_deadline, 'order_deadline')}</Field>
            <Field label="Valid through Cyber Monday">
              <YesNoSeg value={t.same_conditions} onChange={(v) => save({ same_conditions: v })} />
            </Field>
          </div>
          <Field label="Tiers">
            <TiersEditor value={t.tiers} onSave={(tiers) => save({ tiers })} />
          </Field>
        </Group>
        <Group title="Payment">
          <div className="form-grid">
            <Field label="Payment terms granted"><CommitInput value={t.payment_terms} placeholder="e.g. 60 days one-off" onCommit={(v) => save({ payment_terms: v })} /></Field>
            <Field label="Credit limit">{eur(t.credit_limit, 'credit_limit')}</Field>
          </div>
        </Group>
        <Group title="Our order">
          <div className="form-grid">
            <Field label="Portal / order code"><CommitInput value={t.order_channel} placeholder="e.g. MyFoscarini, BLACKFRIDAY26" onCommit={(v) => save({ order_channel: v })} /></Field>
            <Field label="Order number (PO)"><CommitInput value={t.order_po} placeholder="P26… or the Odoo link" onCommit={(v) => save({ order_po: v })} /><Links text={t.order_po} /></Field>
            <Field label="Order amount">{eur(t.order_amount, 'order_amount')}</Field>
          </div>
          <Field label="Notes">
            <CommitInput multiline rows={3} value={t.sellin_notes} onCommit={(v) => save({ sellin_notes: v })} />
            <Links text={t.sellin_notes} />
          </Field>
        </Group>
      </Section>

      <Section k="topup" summary={topupSummary(t)}>
        <Group title="A second order after Black Friday, same conditions">
          <div className="form-grid">
            <Field label="Allowed">
              <YesNoSeg value={t.topup_allowed} onChange={(v) => save({ topup_allowed: v })} />
            </Field>
            <Field label="Deadline">{day(t.topup_deadline, 'topup_deadline')}</Field>
            <Field label="Discount">{pct(t.topup_discount_pct, 'topup_discount_pct')}</Field>
            <Field label="Minimum">{eur(t.topup_minimum, 'topup_minimum')}</Field>
            <Field label="Order number (PO)"><CommitInput value={t.topup_po} placeholder="P26… or the Odoo link" onCommit={(v) => save({ topup_po: v })} /><Links text={t.topup_po} /></Field>
            <Field label="Amount">{eur(t.topup_amount, 'topup_amount')}</Field>
          </div>
          <Field label="Notes"><CommitInput multiline rows={2} value={t.topup_notes} onCommit={(v) => save({ topup_notes: v })} /><Links text={t.topup_notes} /></Field>
        </Group>
      </Section>

      <Section k="notes" summary={t.notes ? noteHead(t.notes, 1, 60) : ''}>
        <CommitInput multiline rows={4} value={t.notes} onCommit={(v) => save({ notes: v })} placeholder="Anything else: follow-ups, who said what, open points." />
        <Links text={t.notes} />
        {t.quotes?.length > 0 && <Quotes quotes={t.quotes} />}
        {t.change_log?.length > 0 && <ChangeLog log={t.change_log} />}
      </Section>
    </div>
  );
}

/** Number field with its unit pinned inside on the right ("%", "€ net"),
 *  visible whether the field is empty or filled. */
function Unit({ unit, children }: { unit: string; children: React.ReactNode }) {
  return (
    <span className="unit-wrap">
      {children}
      <span className="unit">{unit}</span>
    </span>
  );
}

/** Yes / Conditional / No as buttons (like the asks). Clicking the active
 *  one again resets to "unknown", so nothing is pressed = not known yet. */
function YesNoSeg({ value, onChange }: { value: YesNo; onChange: (v: YesNo) => void }) {
  return (
    <span className="seg seg-field">
      {(['yes', 'conditional', 'no'] as YesNo[]).map((v) => (
        <button
          key={v}
          type="button"
          className={v === 'yes' ? 'yes' : v === 'no' ? 'no' : 'mid'}
          aria-pressed={value === v}
          onClick={() => onChange(value === v ? 'unknown' : v)}
        >
          {YESNO_LABEL[v]}
        </button>
      ))}
    </span>
  );
}

/** A sub-group inside a section: small heading, then the fields. Keeps
 *  long sections scannable without a wall of inputs. */
function Group({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grp">
      <div className="grp-title">{title}</div>
      {children}
    </div>
  );
}

/** One section of the terms page, styled like the campaign page: the
 *  uppercase title sits ABOVE the card (coloured dot + icon, with a one-line
 *  summary of what's filled in), and the card carries the section's colour
 *  as a left accent bar — the same colour + icon the register columns use. */
function Section({ k, alias, summary, action, children }: {
  k: BFSectionKey; alias?: string; summary?: string; action?: React.ReactNode; children: React.ReactNode;
}) {
  const s = BF_SECTIONS[k];
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(`bf-sec-${k}`) !== 'closed'; } catch { return true; }
  });
  const toggle = () => {
    setOpen((o) => {
      try { localStorage.setItem(`bf-sec-${k}`, o ? 'closed' : 'open'); } catch { /* private mode */ }
      return !o;
    });
  };
  return (
    <div className="bf-sec" style={{ '--sec': s.color } as React.CSSProperties}>
      <div className="section-head">
        <button type="button" className="section-title bf-sec-title" onClick={toggle} aria-expanded={open}>
          <span className="dot" />
          <Icon name={s.icon} size={13} />
          {s.label}
          {alias && <span className="alias">{alias}</span>}
          {!open && summary && <span className="sum">· {summary}</span>}
          <span className="chev"><Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} /></span>
        </button>
        {action}
      </div>
      {open && (
        <Card className="bf-sec-card">
          <div className="body">{children}</div>
        </Card>
      )}
    </div>
  );
}

/** Title above a card for the right column, same type as the left but neutral. */
function PlainSection({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="bf-sec">
      <div className="section-head"><div className="section-title">{title}</div></div>
      <Card>
        {sub && <div className="sec-sub">{sub}</div>}
        <div className="body">{children}</div>
      </Card>
    </div>
  );
}

const asksSummary = (t: BrandTerms) => {
  const yes = ASKS.filter((a) => t.asks?.[a.key] === 'yes').length;
  const no = ASKS.filter((a) => t.asks?.[a.key] === 'no').length;
  if (!yes && !no) return t.contact ? t.contact.replace(/\s*<.*$/, '') : '';
  return `${yes} agreed · ${no} refused · ${ASKS.length - yes - no} open`;
};

/** "4 agreed · 1 refused · 1 open", leaving out the zeros. */
const asksCount = (t: BrandTerms) => {
  const yes = ASKS.filter((a) => t.asks?.[a.key] === 'yes').length;
  const no = ASKS.filter((a) => t.asks?.[a.key] === 'no').length;
  const open = ASKS.length - yes - no;
  return [yes && `${yes} agreed`, no && `${no} refused`, open && `${open} open`].filter(Boolean).join(' · ');
};

const topupSummary = (t: BrandTerms) =>
  t.topup_allowed === 'yes' ? `allowed${t.topup_deadline ? ` · by ${fmtDay(t.topup_deadline)}` : ''}${num(t.topup_discount_pct) !== null ? ` · ${fmtPct(t.topup_discount_pct)}` : ''}`
    : t.topup_allowed === 'no' ? 'not allowed'
      : t.topup_allowed === 'conditional' ? 'conditional' : '';

const FIELD_LABEL: Record<string, string> = {
  status: 'status', asks: 'asks', contact: 'contact', agent: 'agent', asked_at: 'asked on', answered_at: 'answered on',
  source_url: 'email link', brand_initiated: 'own campaign', participates: 'participates',
  discount_recommended_pct: 'recommended %', discount_max_pct: 'max %', regional_caps: 'regional caps',
  promo_start: 'allowed from', promo_end: 'allowed until', scope: 'scope', sellout_notes: 'shopper discount notes', visibility_conditions: 'visibility conditions',
  our_discount_pct: 'what we ran', our_promo_start: 'went live', extra_discount_pct: 'extra %', tiers: 'tiers',
  minimum_order_net: 'minimum order', order_deadline: 'order deadline', order_channel: 'portal / code',
  payment_terms: 'payment terms', credit_limit: 'credit limit', same_conditions: 'same conditions',
  order_po: 'order number', order_amount: 'order amount', sellin_notes: 'stock order notes', topup_allowed: 'top-up',
  topup_deadline: 'top-up deadline', topup_discount_pct: 'top-up %', topup_minimum: 'top-up minimum',
  topup_po: 'top-up order number', topup_amount: 'top-up amount', topup_notes: 'top-up notes', notes: 'notes', quotes: 'quotes',
};

/** Updates written by the "fill from email" action (Claude or a script), newest first. */
function ChangeLog({ log }: { log: BrandTerms['change_log'] }) {
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border-soft)', paddingTop: 10 }}>
      <div className="small muted" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Filled from emails</div>
      {[...log].reverse().map((e, i) => (
        <div key={i} className="small" style={{ padding: '4px 0', color: 'var(--ink-3)' }}>
          <b style={{ color: 'var(--ink-2)' }}>{fmtDay(e.at.slice(0, 10))}</b>
          {' · '}{e.by.split('@')[0]}
          {e.source && <> · {e.url ? <a className="link" href={e.url} target="_blank" rel="noopener noreferrer">{e.source}</a> : e.source}</>}
          <div className="muted">{e.fields.map((f) => FIELD_LABEL[f] ?? f).join(', ')}</div>
        </div>
      ))}
    </div>
  );
}

function TiersEditor({ value, onSave }: { value: Tier[]; onSave: (tiers: Tier[]) => void }) {
  const [draft, setDraft] = useState<Tier | null>(null);
  const tiers = value ?? [];
  const update = (i: number, patch: Partial<Tier>) => onSave(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <div className="rows-editor">
      {tiers.map((t, i) => (
        <div key={i} className="r" style={{ gridTemplateColumns: '110px 80px 90px 1fr 28px' }}>
          <CommitInput type="number" min={0} className="input" value={String(t.min)} onCommit={(v) => update(i, { min: parseFloat(v || '0') })} />
          <select className="input" value={t.unit} onChange={(e) => update(i, { unit: e.target.value as Tier['unit'] })}>
            <option value="eur">€ net</option>
            <option value="pcs">pieces</option>
          </select>
          <CommitInput type="number" min={0} className="input" value={String(t.extra_pct)} onCommit={(v) => update(i, { extra_pct: parseFloat(v || '0') })} />
          <CommitInput className="input" value={t.note ?? ''} placeholder="note" onCommit={(v) => update(i, { note: v })} />
          <button type="button" className="icon-btn" aria-label="Remove tier" onClick={() => onSave(tiers.filter((_, j) => j !== i))}><Icon name="x" size={12} /></button>
        </div>
      ))}
      {draft ? (
        <div className="r" style={{ gridTemplateColumns: '110px 80px 90px 1fr auto' }}>
          <input className="input" type="number" min={0} placeholder="from" value={draft.min || ''} onChange={(e) => setDraft({ ...draft, min: parseFloat(e.target.value || '0') })} autoFocus />
          <select className="input" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value as Tier['unit'] })}>
            <option value="eur">€ net</option>
            <option value="pcs">pieces</option>
          </select>
          <input className="input" type="number" min={0} placeholder="extra %" value={draft.extra_pct || ''} onChange={(e) => setDraft({ ...draft, extra_pct: parseFloat(e.target.value || '0') })} />
          <input className="input" placeholder="note" value={draft.note ?? ''} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
          <span style={{ display: 'flex', gap: 4 }}>
            <Button variant="primary" onClick={() => { onSave([...tiers, draft]); setDraft(null); }}>Add</Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
          </span>
        </div>
      ) : (
        <div><Button variant="ghost" onClick={() => setDraft({ min: 0, unit: 'eur', extra_pct: 0, note: '' })}><Icon name="plus" size={12} /> Add tier</Button></div>
      )}
      {tiers.length > 0 && <div className="hint">{tiers.map(tierLine).join('   ·   ')}</div>}
    </div>
  );
}

function RegionalCapsEditor({ value, onSave }: { value: RegionalCap[]; onSave: (caps: RegionalCap[]) => void }) {
  const caps = value ?? [];
  const [region, setRegion] = useState('');
  const [pct, setPct] = useState('');
  const add = () => {
    if (!region.trim() || pct === '') return;
    onSave([...caps, { region: region.trim(), max_pct: parseFloat(pct) }]);
    setRegion('');
    setPct('');
  };
  return (
    <div className="rows-editor" style={{ marginBottom: 0 }}>
      {caps.map((c, i) => (
        <div key={i} className="r" style={{ gridTemplateColumns: '1fr 90px 28px' }}>
          <CommitInput className="input" value={c.region} onCommit={(v) => onSave(caps.map((x, j) => (j === i ? { ...x, region: v } : x)))} />
          <CommitInput type="number" min={0} className="input" value={String(c.max_pct)} onCommit={(v) => onSave(caps.map((x, j) => (j === i ? { ...x, max_pct: parseFloat(v || '0') } : x)))} />
          <button type="button" className="icon-btn" aria-label="Remove region" onClick={() => onSave(caps.filter((_, j) => j !== i))}><Icon name="x" size={12} /></button>
        </div>
      ))}
      <div className="r" style={{ gridTemplateColumns: '1fr 90px auto' }}>
        <input className="input" placeholder="Region, e.g. Benelux" value={region} onChange={(e) => setRegion(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input className="input" type="number" min={0} placeholder="max %" value={pct} onChange={(e) => setPct(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <Button variant="ghost" onClick={add} disabled={!region.trim() || pct === ''}>Add</Button>
      </div>
    </div>
  );
}

function Links({ text }: { text: string | null | undefined }) {
  const links = findLinks(text);
  if (!links.length) return null;
  return (
    <div className="bf-links">
      {links.map((l) => (
        <a key={l.url} className="link" href={l.url} target="_blank" rel="noopener noreferrer" title={l.url}>
          {l.label} ↗
        </a>
      ))}
    </div>
  );
}

function Quotes({ quotes, limit }: { quotes: BrandTerms['quotes']; limit?: number }) {
  const [all, setAll] = useState(false);
  const shown = all || !limit ? quotes : quotes.slice(0, limit);
  return (
    <div style={{ marginTop: 10 }}>
      {shown.map((q, i) => (
        <div key={i} className="quote">
          “{q.text}”
          <span className="by">{q.speaker}{q.date ? ` · ${fmtDay(q.date)}` : ''}</span>
        </div>
      ))}
      {limit && quotes.length > limit && !all && (
        <button type="button" className="link small" onClick={() => setAll(true)}>Show {quotes.length - limit} more</button>
      )}
    </div>
  );
}

/** "Saving… / Saved ✓" badge for the field-by-field editor. Sits at the top
 *  of the column and sticks while you scroll; the "Saved" state fades after
 *  a couple of seconds so it only shows right after a change. */
function SaveState({ pending, savedAt }: { pending: boolean; savedAt: number }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!savedAt || pending) return;
    setShow(true);
    const id = window.setTimeout(() => setShow(false), 2200);
    return () => window.clearTimeout(id);
  }, [savedAt, pending]);
  if (!pending && !show) return null;
  return (
    <div className="save-state" aria-live="polite">
      {pending ? <span>Saving…</span> : <span className="saved">Saved ✓</span>}
    </div>
  );
}

/** Sales figures for this brand: what it is worth to us going into the talks. */
function NumbersCard({ year, brandId }: { year: number; brandId: string }) {
  const priority = useBFPriority(year);
  const p = priority.data?.brands[brandId];
  if (!priority.data?.configured || priority.data.error) return null;
  const tiles: { label: string; value: string; sub?: string }[] = [
    { label: 'Sales, last 12 months', value: p?.sales_12m ? fmtEurShort(p.sales_12m) : '—', sub: p?.orders_12m ? `${p.orders_12m} orders` : undefined },
    { label: `Black Friday ${priority.data.bf_year}`, value: p?.bf_sales ? fmtEurShort(p.bf_sales) : '—', sub: p?.bf_orders ? `${p.bf_orders} orders` : undefined },
    { label: 'Gross margin', value: p?.gross_margin_pct_12m != null ? `${p.gross_margin_pct_12m}%` : '—', sub: 'last 12 months' },
    { label: 'On the site now', value: siteDiscountLine(p) || '—', sub: 'discount shoppers see' },
  ];
  return (
    <div className="bf-sec">
      <div className="section-head"><div className="section-title">Where we stand</div></div>
      <div className="numbers">
        {tiles.map((t) => (
          <div key={t.label} className="tile">
            <div className="v">{t.value}</div>
            <div className="l">{t.label}</div>
            {t.sub && <div className="s">{t.sub}</div>}
          </div>
        ))}
        {priority.data.margins_through && <div className="foot">Sales through {fmtDay(priority.data.margins_through)} · stock items can carry a deeper discount</div>}
      </div>
    </div>
  );
}

/** Earlier seasons for this brand, newest first. For the negotiation only. */
function PrepCard({ rows }: { rows: BrandTerms[] }) {
  // Collapsed by default — Robbie opens a year (or all) when he needs it.
  const [openYears, setOpenYears] = useState<Set<number>>(() => new Set());
  const allOpen = rows.length > 0 && rows.every((r) => openYears.has(r.year));
  const toggleYear = (y: number) => setOpenYears((prev) => {
    const next = new Set(prev);
    if (next.has(y)) next.delete(y); else next.add(y);
    return next;
  });
  return (
    <div className="bf-sec">
      <div className="section-head">
        <div className="section-title">Previous seasons</div>
        {rows.length > 1 && (
          <button type="button" className="section-action" onClick={() => setOpenYears(allOpen ? new Set() : new Set(rows.map((r) => r.year)))}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>
      <Card>
        <div className="body prep">
          {rows.length === 0 ? (
            <div className="hint">No history for this brand yet.</div>
          ) : rows.map((r) => <PrepYear key={r.id} r={r} open={openYears.has(r.year)} onToggle={() => toggleYear(r.year)} />)}
        </div>
      </Card>
    </div>
  );
}

/** One line per year when closed: "2025 · ran 25% · max 25% · ordered €92k". */
const yearGlance = (r: BrandTerms) => {
  const bits: string[] = [];
  if (num(r.our_discount_pct) !== null) bits.push(`ran ${fmtPct(r.our_discount_pct)}`);
  else if (num(r.discount_max_pct) !== null) bits.push(`max ${fmtPct(r.discount_max_pct)}`);
  else if (num(r.discount_recommended_pct) !== null) bits.push(`rec ${fmtPct(r.discount_recommended_pct)}`);
  if (num(r.extra_discount_pct) !== null) bits.push(`+${fmtPct(r.extra_discount_pct)} stock`);
  if (num(r.order_amount) !== null) bits.push(`ordered ${fmtEurShort(r.order_amount)}`);
  if (r.topup_allowed === 'yes') bits.push('top-up');
  return bits.join(' · ');
};

function PrepYear({ r, open: expanded, onToggle }: { r: BrandTerms; open: boolean; onToggle: () => void }) {
  const [open, setOpen] = useState(false);
  const so = selloutLine(r) || noteHead(r.sellout_notes, 1, 160);
  const si = sellinLine(r) || noteHead(r.sellin_notes, 2, 160);
  const ours = [fmtPct(r.our_discount_pct), r.our_promo_start ? `from ${fmtDay(r.our_promo_start)}` : ''].filter(Boolean).join(' ');
  const topup = r.topup_allowed === 'unknown' ? '' : [
    YESNO_LABEL[r.topup_allowed],
    r.topup_deadline ? `by ${fmtDay(r.topup_deadline)}` : '',
    num(r.topup_discount_pct) !== null ? fmtPct(r.topup_discount_pct) : '',
  ].filter(Boolean).join(' · ');
  const order = [r.order_po, num(r.order_amount) !== null ? fmtEurShort(r.order_amount) : ''].filter(Boolean).join(' · ');
  const longText = [r.sellout_notes, r.sellin_notes, r.topup_notes, r.notes].filter(Boolean).join('\n\n');
  return (
    <div className={`prep-year${expanded ? '' : ' closed'}`}>
      <h4 onClick={onToggle} role="button" aria-expanded={expanded}>
        <span className="y">
          <span className="chev"><Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={12} /></span>
          {r.year}
          {!expanded && <span className="glance">{yearGlance(r) || STATUS_LABEL[r.status].toLowerCase()}</span>}
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          {r.source_url && <a className="link small" href={r.source_url} target="_blank" rel="noopener noreferrer">thread</a>}
          <StatusPill status={r.status} />
        </span>
      </h4>
      {expanded && <>
      <dl>
        {so && <><dt>Allowed</dt><dd>{so}</dd></>}
        {ours && <><dt>We ran</dt><dd>{ours}</dd></>}
        {si && <><dt>Stock order</dt><dd>{si}</dd></>}
        {r.tiers?.length > 1 && <><dt>Tiers</dt><dd>{r.tiers.map(tierLine).join('\n')}</dd></>}
        {r.payment_terms && <><dt>Payment</dt><dd>{r.payment_terms}</dd></>}
        {r.same_conditions !== 'unknown' && <><dt>During BFCM</dt><dd>{YESNO_LABEL[r.same_conditions]}</dd></>}
        {topup && <><dt>Top-up</dt><dd>{topup}</dd></>}
        {order && <><dt>Ordered</dt><dd>{order.replace(/\s*[—-]?\s*https?:\/\/\S+/g, '')}<Links text={r.order_po} /></dd></>}
        {r.contact && <><dt>Contact</dt><dd>{r.contact}</dd></>}
        {!so && !si && !ours && !topup && !order && !r.payment_terms && <><dt> </dt><dd className="muted">No structured terms recorded.</dd></>}
      </dl>
      {longText && (
        <>
          <button type="button" className="link small" style={{ marginTop: 8 }} onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide notes' : 'Show notes'}
          </button>
          {open && <div className="more">{longText}</div>}
        </>
      )}
      {r.quotes?.length > 0 && <Quotes quotes={r.quotes} limit={2} />}
      </>}
    </div>
  );
}
